import { useCallback, useState } from 'react';
import { Button, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AppWindow, Plus, TriangleAlert } from 'lucide-react';

import Can from '../components/guards/Can';
import GrantAccessModal from '../components/member-studios/GrantAccessModal';
import RemoveAccessConfirm from '../components/member-studios/RemoveAccessConfirm';
import StudioRow from '../components/member-studios/StudioRow';
import { MEMBER_STATUS } from '../constants/copy';
import { PERMISSIONS } from '../constants/permissions';
import { getFirstName } from '../utils/memberDisplay';
import { useMemberStudios } from '../hooks/useMemberStudios';

/**
 * Studios tab of the member detail: one row per studio the member reaches (and why), the
 * Grant access modal, and the per-member edit of direct grants. Admin / Owner rows only carry
 * their type tag.
 *
 * @param {object} props
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 */
function MemberStudiosTab({ member }) {
  const { t } = useTranslation('people-access');
  const isRemoved = member.status === MEMBER_STATUS.REMOVED;
  const { studios, grantableStudios, isSuspended, isLoading, error, save, grant, removeAccess } = useMemberStudios(member);
  const [openId, setOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isGrantOpen, setIsGrantOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  const handleToggleOpen = useCallback((id) => {
    setOpenId((current) => (current === id ? null : id));
    setEditingId((current) => (current === id ? null : current));
  }, []);
  const handleEdit = useCallback((id) => {
    setOpenId(id);
    setEditingId(id);
  }, []);
  const handleStopEditing = useCallback(() => setEditingId(null), []);
  const handleOpenGrant = useCallback(() => setIsGrantOpen(true), []);
  const handleCloseGrant = useCallback(() => setIsGrantOpen(false), []);
  const handleCloseRemove = useCallback(() => setRemoving(null), []);
  const handleRequestRemove = useCallback((id) => setRemoving(studios.find((s) => s.id === id) ?? null), [studios]);

  const handleGrant = useCallback(
    async (studioId) => {
      const ok = await grant(studioId);
      if (ok) {
        setOpenId(studioId);
        setEditingId(studioId);
      }
      return ok;
    },
    [grant],
  );

  const handleRemove = useCallback(
    async (studioId) => {
      const ok = await removeAccess(studioId);
      if (ok) {
        setEditingId(null);
        setOpenId(null);
      }
      return ok;
    },
    [removeAccess],
  );

  if (isRemoved) {
    return (
      <div data-testid="member-tab-studios">
        <StatusMessage icon={AppWindow} title={t('studios.removed.title')} subtitle={t('studios.removed.subtitle')} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="member-studios-loading">
        <Skeleton height="72px" borderRadius="12px" />
        <Skeleton height="72px" borderRadius="12px" />
      </div>
    );
  }

  if (error) {
    return <StatusMessage icon={TriangleAlert} title={t('studios.loadError')} subtitle={t('memberDetail.state.failed.subtitle')} />;
  }

  const canGrant = grantableStudios.length > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="member-tab-studios">
      {canGrant ? (
        <div className="flex justify-end">
          <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
            <Button variant="secondary" size="sm" leftIcon={Plus} onClick={handleOpenGrant} disabled={isSuspended}>
              {t('studios.grantAccess')}
            </Button>
          </Can>
        </div>
      ) : null}

      {isSuspended ? (
        <p className="text-xs text-content-muted" data-testid="studios-suspended-note">
          {t('studios.suspended.note', { name: getFirstName(member.displayName) })}
        </p>
      ) : null}

      {studios.length === 0 ? (
        <StatusMessage icon={AppWindow} title={t('studios.empty.title')} subtitle={t('studios.empty.subtitle')} />
      ) : (
        studios.map((studio) => (
          <StudioRow
            key={studio.id}
            studio={studio}
            memberName={member.displayName}
            isOpen={openId === studio.id}
            isEditing={editingId === studio.id}
            onToggleOpen={handleToggleOpen}
            onEdit={handleEdit}
            onStopEditing={handleStopEditing}
            onSave={save}
            onRemoveAccess={handleRequestRemove}
          />
        ))
      )}

      {studios.length > 0 && !canGrant ? (
        <p className="text-center text-xs text-content-muted">{t('studios.allGranted')}</p>
      ) : null}

      <GrantAccessModal
        isOpen={isGrantOpen}
        studios={grantableStudios}
        memberName={member.displayName}
        onClose={handleCloseGrant}
        onGrant={handleGrant}
      />
      <RemoveAccessConfirm studio={removing} memberName={member.displayName} onClose={handleCloseRemove} onConfirm={handleRemove} />
    </div>
  );
}

export default MemberStudiosTab;
